import { useNow } from "./clock";
import { StatusPill } from "./primitives";

/**
 * Components a generic kit does not have.
 *
 * This is the half of the design system with no equivalent anywhere, and the reason the
 * shell was hand-written rather than assembled. docs/03 §8 and §13.2.
 */

const HOUR = 3_600_000;
const WINDOW = 24 * HOUR;

/**
 * How long the free-form window has left.
 *
 * The urgency ramp is the one place colour changes over time: neutral above six hours,
 * amber from one to six, crimson under one. It never blinks and it never pulses — a
 * countdown that pulses for an hour is a countdown people learn to ignore. docs/03 §4.
 */
export function ServiceWindowMeter({ expiresAt }: { expiresAt: number | null }) {
  // A row reading "4h 12m" changes once a minute; only the last hour shows seconds. The
  // coarse read decides which band we are in, then we subscribe at that band's precision —
  // the difference between fifty renders a second and fifty a minute. docs/08 §3 P3.
  const coarse = useNow("minute");
  const now = useNow(expiresAt !== null && expiresAt - coarse < HOUR ? "second" : "minute");

  if (expiresAt === null) return <span className="swm neutral">no window</span>;

  const remaining = expiresAt - now;
  if (remaining <= 0) {
    return (
      <span className="swm crimson">
        <span className="swm-track">
          <span className="swm-fill" style={{ transform: "scaleX(0)" }} />
        </span>
        window closed
      </span>
    );
  }

  const tone = remaining < HOUR ? "crimson" : remaining < 6 * HOUR ? "amber" : "neutral";
  const h = Math.floor(remaining / HOUR);
  const m = Math.floor((remaining % HOUR) / 60_000);
  const s = Math.floor((remaining % 60_000) / 1000);

  return (
    <span className={`swm ${tone}`} title="Time left to reply without a template">
      <span className="swm-track">
        {/* scaleX, not inline-size: this ticks once a second on every visible row. */}
        <span
          className="swm-fill"
          style={{ transform: `scaleX(${Math.min(1, remaining / WINDOW).toFixed(4)})` }}
        />
      </span>
      {h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m ${String(s).padStart(2, "0")}s`}
    </span>
  );
}

/**
 * Whether a playbook is driving this conversation.
 *
 * "Paused — you took over" is the string that matters: an agent must know their reply
 * silenced the automation, or they will expect a follow-up that never comes.
 */
export function AutomationBadge({ state }: { state: string }) {
  if (state === "active") return <StatusPill tone="brand">Playbook active</StatusPill>;
  if (state === "paused_human") return <StatusPill>Paused — you took over</StatusPill>;
  if (state === "paused_optout") return <StatusPill tone="danger">Stopped — opted out</StatusPill>;
  return null;
}

/** queued → sent → delivered → read → failed, with the failure in plain language. */
export function DeliveryReceipt({
  status,
  failure,
}: {
  status: string | null;
  failure?: string | null;
}) {
  if (failure) return <span className="bub-receipt failed">{failure}</span>;
  return <span className="bub-receipt">{status ?? "sent"}</span>;
}
